"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "motion/react"
import Image from "next/image"
import type {
	HeroSliderBlock as HeroSliderBlockProps,
	Media,
} from "@/payload-types"
import { CMSLink } from "@/components/Link"
import { cn } from "@/utilities/ui"
import { ChevronLeft, ChevronRight } from "lucide-react"

export const HeroSliderBlock: React.FC<HeroSliderBlockProps> = ({
	slides,
	settings,
}) => {
	const [currentIndex, setCurrentIndex] = useState(0)
	const [direction, setDirection] = useState(0)
	const timerRef = useRef<NodeJS.Timeout | null>(null)
	const containerRef = useRef<HTMLElement>(null)

	const autoPlay = settings?.autoPlay ?? true
	const interval = settings?.interval ?? 5000
	const showArrows = settings?.showArrows ?? true
	const showDots = settings?.showDots ?? true
	const transitionEffect = settings?.transitionEffect ?? "fade"

	const nextSlide = useCallback(() => {
		setDirection(1)
		setCurrentIndex((prevIndex) =>
			prevIndex === (slides?.length || 0) - 1 ? 0 : prevIndex + 1,
		)
	}, [slides?.length])

	const prevSlide = useCallback(() => {
		setDirection(-1)
		setCurrentIndex((prevIndex) =>
			prevIndex === 0 ? (slides?.length || 0) - 1 : prevIndex - 1,
		)
	}, [slides?.length])

	const goToSlide = (index: number) => {
		setDirection(index > currentIndex ? 1 : -1)
		setCurrentIndex(index)
	}

	useEffect(() => {
		if (autoPlay && slides && slides.length > 1) {
			timerRef.current = setInterval(nextSlide, interval)
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current)
		}
	}, [autoPlay, interval, nextSlide, slides])

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!containerRef.current?.contains(document.activeElement)) {
				return
			}

			if (e.key === "ArrowLeft") {
				prevSlide()
			} else if (e.key === "ArrowRight") {
				nextSlide()
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [nextSlide, prevSlide])

	if (!slides || slides.length === 0) return null

	const variants = {
		enter: (direction: number) => ({
			x: transitionEffect === "slide" ? (direction > 0 ? "100%" : "-100%") : 0,
			opacity: transitionEffect === "fade" ? 0 : 1,
		}),
		center: {
			zIndex: 1,
			x: 0,
			opacity: 1,
		},
		exit: (direction: number) => ({
			zIndex: 0,
			x: transitionEffect === "slide" ? (direction < 0 ? "100%" : "-100%") : 0,
			opacity: transitionEffect === "fade" ? 0 : 1,
		}),
	}

	const cinematicEase = [0.22, 1, 0.36, 1] as [number, number, number, number]

	const transition = {
		duration: 0.8,
		ease: cinematicEase,
	}

	const currentSlide = slides[currentIndex]

	return (
		<section
			ref={containerRef}
			aria-roledescription="carousel"
			aria-label="Galería de imágenes destacadas"
			className="relative w-full h-[600px] md:h-[800px] lg:h-[90vh] overflow-hidden bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/20"
		>
			<AnimatePresence initial={false} custom={direction} mode="popLayout">
				<motion.div
					key={currentIndex}
					custom={direction}
					variants={variants}
					initial="enter"
					animate="center"
					exit="exit"
					transition={transition}
					className="absolute inset-0 w-full h-full"
				>
					{/* Background Image */}
					{currentSlide.image && typeof currentSlide.image === "object" && (
						<Image
							src={(currentSlide.image as Media).url || ""}
							alt={(currentSlide.image as Media).alt || ""}
							fill
							priority={currentIndex === 0}
							loading={currentIndex === 0 ? "eager" : "lazy"}
							className="object-cover"
							sizes="100vw"
						/>
					)}

					{/* Overlay */}
					<div className="absolute inset-0 bg-black/40" />

					{/* Content */}
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="container px-4 text-center text-white">
							<motion.h1
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.3, duration: 0.8, ease: cinematicEase }}
								className="text-4xl md:text-6xl lg:text-8xl font-bold mb-6 tracking-tight"
							>
								{currentSlide.headline}
							</motion.h1>
							{currentSlide.subheadline && (
								<motion.p
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{
										delay: 0.5,
										duration: 0.8,
										ease: cinematicEase,
									}}
									className="text-lg md:text-2xl mb-10 max-w-3xl mx-auto font-light leading-relaxed"
								>
									{currentSlide.subheadline}
								</motion.p>
							)}
							{currentSlide.cta && currentSlide.cta.label && (
								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{
										delay: 0.7,
										duration: 0.8,
										ease: cinematicEase,
									}}
								>
									<CMSLink
										type="custom"
										url={currentSlide.cta.link}
										label={currentSlide.cta.label}
										appearance={
											(currentSlide.cta.style === "primary"
												? "default"
												: currentSlide.cta.style) as
												| "default"
												| "outline"
												| "secondary"
												| "ghost"
												| "link"
										}
										size="lg"
										className="min-w-[180px]"
									/>
								</motion.div>
							)}
						</div>
					</div>
				</motion.div>
			</AnimatePresence>

			{/* Navigation Arrows */}
			{showArrows && slides.length > 1 && (
				<>
					<button
						type="button"
						onClick={prevSlide}
						className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-primary text-white transition-all backdrop-blur-sm border border-white/10 hover:border-primary group focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
						aria-label="Slide anterior"
					>
						<ChevronLeft className="w-6 h-6 md:w-8 md:h-8 transition-transform group-hover:-translate-x-0.5" />
					</button>
					<button
						type="button"
						onClick={nextSlide}
						className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-primary text-white transition-all backdrop-blur-sm border border-white/10 hover:border-primary group focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
						aria-label="Slide siguiente"
					>
						<ChevronRight className="w-6 h-6 md:w-8 md:h-8 transition-transform group-hover:translate-x-0.5" />
					</button>
				</>
			)}

			{/* Dots */}
			{showDots && slides.length > 1 && (
				<div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
					{slides.map((slide, index) => (
						<button
							key={slide.id || index}
							type="button"
							tabIndex={currentIndex === index ? 0 : -1}
							onClick={() => goToSlide(index)}
							className={cn(
								"w-2.5 h-2.5 rounded-full transition-all duration-500 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none",
								currentIndex === index
									? "bg-primary w-12"
									: "bg-white/30 hover:bg-primary/60",
							)}
							aria-label={`Ir al slide ${index + 1}`}
							aria-current={currentIndex === index ? "true" : "false"}
						/>
					))}
				</div>
			)}
		</section>
	)
}
